import React from 'react';

type UseDropdownReturn = [
  boolean,
  (event: ToggleEvent, value?: boolean) => void,
  (event: React.FocusEvent<HTMLElement>) => void,
  React.RefObject<HTMLElement>,
  React.RefObject<HTMLElement>,
  React.Dispatch<React.SetStateAction<boolean>>,
];

type ToggleEvent =
  | React.MouseEvent<HTMLElement>
  | React.KeyboardEvent<HTMLElement>
  | undefined;

export default function useDropdown(
  defaultState: boolean = false,
  timeout = 0,
  containerCheck = true,
): UseDropdownReturn {
  const containerRef = React.useRef<HTMLElement>(null);
  const buttonRef = React.useRef<HTMLElement>(null);
  const [dropdown, setDropdown] = React.useState<boolean>(defaultState);

  const toggle = React.useCallback(
    (event: ToggleEvent, value?: boolean) => {
      if (
        event &&
        ('button' in event ||
          (event.type === 'keyup' &&
            ['Enter', ' '].includes((event as React.KeyboardEvent).key)))
      ) {
        event.preventDefault();
      }
      setDropdown(value !== undefined ? value : !dropdown);
    },
    [setDropdown, dropdown],
  );

  const onBlur = (event: React.FocusEvent<HTMLElement>) => {
    const target = event.relatedTarget || document.activeElement;
    setTimeout(function () {
      if (containerCheck) {
        if (
          containerRef.current &&
          !containerRef.current.contains(target as Node) &&
          buttonRef.current !== target
        ) {
          setDropdown(false);
        }
      } else {
        if (buttonRef.current !== target) {
          setDropdown(false);
        }
      }
    }, timeout);
  };

  return [
    dropdown,
    toggle,
    onBlur,
    containerRef,
    buttonRef,
    setDropdown,
  ] as const;
}
